import React, { useMemo, useState } from 'react';
import { Card, Empty, Segmented, Tag } from 'antd';
import dayjs from 'dayjs';
import {
  formatQuestionnaireAnswer,
  getQuestionnaireSummary,
  hasQuestionnaireAnswer,
  QUESTION_TYPE_LABELS,
} from '../commons/partnerQuestionnaire.helpers';

const FILTER_OPTIONS = [
  { label: 'All questions', value: 'all' },
  { label: 'Answered only', value: 'answered' },
  { label: 'Unanswered only', value: 'unanswered' },
];

function QuestionRow({ item, index }) {
  const answer = formatQuestionnaireAnswer(item.response_value, item.question_type);
  const answered = hasQuestionnaireAnswer(item.response_value);
  const typeLabel = QUESTION_TYPE_LABELS[item.question_type] || item.question_type || 'Unknown';

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              Q{index + 1}
            </span>
            <Tag className="m-0">{typeLabel}</Tag>
            {item.is_mandatory ? (
              <Tag color="red" className="m-0">
                Mandatory
              </Tag>
            ) : (
              <Tag color="default" className="m-0">
                Optional
              </Tag>
            )}
          </div>
          <p className="text-sm font-semibold leading-6 text-gray-900">{item.question_text}</p>
        </div>
        {item.answered_at ? (
          <p className="shrink-0 text-xs text-gray-400">
            Answered {dayjs(item.answered_at).format('DD MMM YYYY, hh:mm A')}
          </p>
        ) : null}
      </div>

      <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
          Partner response
        </p>
        {answered ? (
          <p className="whitespace-pre-wrap text-sm leading-6 text-gray-900">{answer}</p>
        ) : (
          <p className="text-sm italic text-gray-400">Not answered</p>
        )}
      </div>
    </div>
  );
}

export default function PartnerQuestionnaireTab({ items = [] }) {
  const [filter, setFilter] = useState('all');
  const questions = Array.isArray(items) ? items : [];
  const summary = useMemo(() => getQuestionnaireSummary(questions), [questions]);

  const filteredQuestions = useMemo(() => {
    if (filter === 'answered') {
      return questions.filter((item) => hasQuestionnaireAnswer(item.response_value));
    }
    if (filter === 'unanswered') {
      return questions.filter((item) => !hasQuestionnaireAnswer(item.response_value));
    }
    return questions;
  }, [filter, questions]);

  if (questions.length === 0) {
    return (
      <Card className="rounded-xl border border-gray-100">
        <Empty description="No questionnaire data found for this partner." />
      </Card>
    );
  }

  return (
    <div className="space-y-5 pt-2">
      <div>
        <h3 className="text-xl font-semibold text-gray-900">Partner Questionnaire</h3>
        <p className="mt-1 text-sm text-gray-500">
          Business profile responses captured during partner onboarding in the Partner portal.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Total questions</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{summary.total}</p>
        </div>
        <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">Answered</p>
          <p className="mt-1 text-2xl font-bold text-emerald-800">{summary.answered}</p>
        </div>
        <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-amber-700">Unanswered</p>
          <p className="mt-1 text-2xl font-bold text-amber-800">{summary.unanswered}</p>
        </div>
        <div className="rounded-xl border border-red-100 bg-red-50/60 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-red-700">Mandatory pending</p>
          <p className="mt-1 text-2xl font-bold text-red-800">{summary.mandatoryUnanswered}</p>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-500">
          Showing {filteredQuestions.length} of {questions.length} questions
        </p>
        <Segmented
          value={filter}
          options={FILTER_OPTIONS}
          onChange={setFilter}
        />
      </div>

      {filteredQuestions.length === 0 ? (
        <Card className="rounded-xl border border-gray-100">
          <Empty description="No questions match the selected filter." />
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredQuestions.map((item) => {
            const questionIndex = questions.findIndex(
              (question) => question.question_id === item.question_id
            );

            return (
              <QuestionRow
                key={item.question_id ?? item.question_text}
                item={item}
                index={questionIndex >= 0 ? questionIndex : 0}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
